/* eslint-disable */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFeatures, useFormContext } from "@/providers";
import { useLocation } from "react-router-dom";
import { CheckboxList } from "./Features/CheckboxList";
import { warningPopup } from "@/helpers";
import { axiosInstance } from "@/helpers/axios";

export const FeatureForm = () => {
    const location = useLocation();
    const { listId = "" } = location.state || {};
    const { features } = useFeatures()
    const [subFeaturesList, setSubFeaturesList] = useState({});
    const [selectedFeatureIDs, setSelectedFeatureIDs] = useState({});
    const { errors, setValue, watch, fieldRefs } = useFormContext();
    const featuresBlock = watch('features');

    const populateFeatures = useCallback(async () => {
        if (featuresBlock?.length > 0 && listId) {
            // Loop through featuresBlock to populate subfeatures and feature IDs
            for (const [index, feature] of featuresBlock.entries()) {
                try {
                    // Set selected feature IDs ensuring uniqueness
                    setSelectedFeatureIDs((prev) => {
                        const updated = Array.isArray(prev) ? [...prev, feature?.feature_id] : [feature?.feature_id];
                        return [...new Set(updated)]; // Remove duplicates
                    });
                    // Fetch subfeatures for the feature
                    const response = await axiosInstance.get(`/feature/${feature?.feature_id}`);

                    // Update subfeatures list
                    setSubFeaturesList((prev) => ({
                        ...prev,
                        [index]: response?.data?.data?.feature_detail || [],
                    }));
                } catch (error) {
                    console.error("Error fetching feature details:", error);
                }
            }
        }
    }, [])

    useEffect(() => {
        featuresBlock?.length > 0 && listId && populateFeatures();
    }, []);

    // Add a new block
    const addBlock = () => {
        const newBlock = {
            id: "", // Assign a unique ID to the new block
            feature_id: "", // Initialize feature_id as empty
            sub_features: [],
        };
        setValue("features", [...(featuresBlock || []), newBlock]);
    };

    // Handle feature selection change
    const handleBlockChange = useCallback(async (e) => {
        const value = e.target.value;
        const index = parseInt(e.target.dataset.index, 10);

        setSelectedFeatureIDs((prev) => ({
            ...prev,
            [index]: value,
        }));

        try {
            const response = await axiosInstance.get(`/feature/${value}`);

            setSubFeaturesList((prev) => ({
                ...prev,
                [index]: response.data.data.feature_detail || [],
            }));
        } catch (error) {
            console.error("Error fetching feature details:", error);
        }

        // Update the selected feature ID in the features block
        const updatedBlocks = [...featuresBlock];
        updatedBlocks[index].id = value;
        setValue('features', updatedBlocks);

    }, [featuresBlock]);

    const handleSubblockChange = useCallback((e, index) => {
        e.preventDefault();
        const checked = e.target.checked;
        const subFeatureID = parseInt(e.target.id);
        const updatedBlocks = [...featuresBlock];
        const checkboxes = updatedBlocks[index].sub_features;

        if (!updatedBlocks[index]?.sub_features) {
            updatedBlocks[index].sub_features = [];
        }

        if (checked) {
            // Add the sub-feature if checked
            checkboxes?.push(subFeatureID);
        } else {
            // Remove the sub-feature if unchecked
            if (updatedBlocks[index].sub_features?.length === 1) {
                warningPopup('At least one feature must be selected from this category!');
                return
            }
            updatedBlocks[index].sub_features = checkboxes.filter(
                (id) => id.toString() !== subFeatureID.toString()
            );
        }

        setValue("features", updatedBlocks);
    }, [featuresBlock, setValue]);

    // Remove a block
    const removeBlock = (blockId) => {
        const updatedBlocks = featuresBlock.filter((block) => {
            return block.id.toString() !== blockId.toString()
        });

        setValue("features", updatedBlocks);
        setSelectedFeatureIDs((prev) => {
            const newState = { ...prev };
            Object.keys(newState).forEach((key) => {
                if (newState[key] === blockId) {
                    delete newState[key];
                }
            });
            return newState;
        });

        setSubFeaturesList((prev) => {
            const newState = { ...prev };
            delete newState[blockId];
            return newState;
        });
    };

    const renderedFeatureBlocks = useMemo(() => {
        return featuresBlock?.map((block, index) => {
            const isFeatureSelected = Boolean(block.id);
            return (
                <div key={block.id || index} className="md:col-span-12" ref={(el) => (fieldRefs.current[`features.${index}.${block.id}`] = el)}>
                    {/* Feature Selector */}
                    <div className="text-left">
                        <label>Feature</label>
                        {isFeatureSelected ? (
                            <div className="flex items-center w-full gap-4 px-3 py-3 bg-gray-100 border rounded-md border-input">
                                {features.find(f => f.id.toString() === block.id.toString())?.heading}
                            </div>
                        ) : (
                            <select
                                value={selectedFeatureIDs[index] || block?.id}
                                onChange={handleBlockChange}
                                data-index={index}
                                className="flex items-center w-full gap-4 px-3 py-3 border rounded-md border-input"
                            >
                                <option disabled value="">
                                    All categories
                                </option>
                                {features?.map((feature) => {
                                    // Check if feature is already selected in any other block
                                    const isAlreadySelected = featuresBlock.some(
                                        (b, i) => i !== index && b.id === feature.id
                                    );

                                    return (
                                        <option
                                            key={feature.id}
                                            value={feature.id}
                                            disabled={isAlreadySelected}
                                        >
                                            {feature.heading}
                                        </option>
                                    )
                                })}
                            </select>
                        )}
                        {errors?.features?.[index]?.id && (
                            <p className="text-red-500">{errors?.features?.[index]?.id.message}</p>
                        )}
                    </div>

                    {/* Show subfeatures only if a feature is selected */}
                    {isFeatureSelected && (
                        <CheckboxList
                            subFeaturesList={subFeaturesList}
                            featuresBlock={featuresBlock}
                            index={index}
                            handleSubblockChange={handleSubblockChange}
                            errors={errors}
                        />
                    )}

                    {featuresBlock.length > 0 && (
                        <button
                            type="button"
                            onClick={() => removeBlock(block.id)}
                            className="border border-[#3b4249] font-medium bg-[#8ec639] rounded-md text-white px-8 py-3"
                        >
                            Remove
                        </button>
                    )}
                </div>
            );
        });
    }, [featuresBlock, selectedFeatureIDs, handleBlockChange, subFeaturesList, handleSubblockChange, errors, features]);

    return (
        <div className="grid gap-4 p-4 bg-white rounded-lg md:grid-cols-12">
            <h3 className="text-xl font-bold text-center md:col-span-12">Features</h3>
            {renderedFeatureBlocks}
            {/* Add and Remove Block Buttons */}
            <div className="flex items-end justify-end gap-4 text-left md:col-span-12">
                <button
                    type="button"
                    onClick={addBlock}
                    className="border border-[#3b4249] font-medium bg-[#8ec639] rounded-md text-white px-8 py-3"
                >
                    Add New Category
                </button>
            </div>
        </div>
    );
};